import React from 'react';
import { withRouter } from 'react-router-dom';
import { get, find, set, cloneDeep, isEmpty, map, noop, every, forEach, filter, isNil, toNumber } from 'lodash';

import { memberDetailsPayBalanceSetAmount as MemberDetailsPayBalanceSetAmountView } from 'ui/views/widgets/memberDetailsPayBalanceSetAmount';
import { MemberDetailsPayBalanceSetAmountService } from 'services/memberDetailsPayBalanceSetAmount';

// React view component to render
import { SERVICE_RESPONSE_STATUS } from 'constants/common';


/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberDetailsPayBalanceSetAmount } from 'ui/containers/MemberDetailsPayBalanceSetAmount';
 *
 * // render
 * <MemberDetailsPayBalanceSetAmount propName='propValue'/>
 */
class _MemberDetailsPayBalanceSetAmount extends React.Component {

	/**
	 * @param {object} props - props
	 */
	constructor( props ) {
		super( props );

		// bind
		this.handleMemberSelect = this.handleMemberSelect.bind( this );
		this.handleRadioFieldSelect = this.handleRadioFieldSelect.bind( this );
		this.handleDefinedAmountChange = this.handleDefinedAmountChange.bind( this );

		// component state
		this.state = {
			isFetching: true,
			networkStatus: undefined,
			componentData: undefined,
		};

		// create service instance
		this.memberDetailsPayBalanceSetAmountService = new MemberDetailsPayBalanceSetAmountService();

		// start data fetch
		this.fetchComponentData();
	}

	/*********************************************/

	/**
	 * @desc Fetch component response.
	 */
	async fetchComponentData() {
		log.debug( 'MemberDetailsPayBalanceSetAmount.fetchComponentData()' );

		// cancel ongoing request
		this.memberDetailsPayBalanceSetAmountService.cancel();

		// fetch component data
		const { status, data, } = await this.memberDetailsPayBalanceSetAmountService.fetchComponentData( {
			url: this.props.widget.url,
			data: this.props.requestPayload,
		} );

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );
		} else {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				componentData: data,
			}, () => {
				this.executeOnChangeCallback( get( this.state, 'componentData.members', [] ) );
			} );
		}
	}

	/**
	 * @desc Handle when member checkbox changes
	 */
	handleMemberSelect( { member } = {} ) {
		log.debug( 'MemberDetailsPayBalanceSetAmount.handleMemberSelect()' );

		// update selected
		const componentData = cloneDeep( this.state.componentData );
		const _member = find( componentData.members, { id: member.id } );
		set( _member, 'selected', ! _member.selected );

		// update state
		this.setState( {
			componentData,
		}, () => {
			this.executeOnChangeCallback( get( this.state, 'componentData.members', [] ) );
		} );
	}

	/**
	 * @desc Handle when field radio is selected
	 */
	handleRadioFieldSelect( { member, field } = {} ) {
		log.debug( 'MemberDetailsPayBalanceSetAmount.handleRadioFieldSelect()', { member, field } );

		// update selected
		const componentData = cloneDeep( this.state.componentData );
		const _member = find( componentData.members, { id: member.id } );

		if( 'amount' === field.name ) {
			set( _member, 'amount.selected', true );
			set( _member, 'definedAmount.selected', false );
		} else if( 'definedAmount' === field.name ) {
			set( _member, 'definedAmount.selected', true );
			set( _member, 'amount.selected', false );
		} else {
			return;
		}

		// update state
		this.setState( {
			componentData,
		}, () => {
			this.executeOnChangeCallback( get( this.state, 'componentData.members', [] ) );
		} );

	}

	/**
	 * @desc Handle when amount is entered in the input
	 */
	handleDefinedAmountChange( { member, field, value } = {} ) {
		log.debug( 'MemberDetailsPayBalanceSetAmount.handleDefinedAmountChange()' );

		// test if `value` is a number
		if( ! isEmpty( value ) && false === /^\d+(\.\d*)?$/.test( value ) ) {
			return; // no-op
		}

		// update selected
		const componentData = cloneDeep( this.state.componentData );
		const _member = find( componentData.members, { id: member.id } );
		set( _member, `${ field.name }.value`, value );

		// update state
		this.setState( {
			componentData,
		}, () => {
			this.executeOnChangeCallback( get( this.state, 'componentData.members', [] ) );
		} );

	}

	/**
	 * @desc Inform parent about the change
	 */
	executeOnChangeCallback( members = [] ) {
		log.debug( 'MemberDetailsPayBalanceSetAmount.executeOnChangeCallback()' );

		// check if selection is valid
		let isValid = true;

		// if no member is selected, selection is invalid
		if( every( members, member => false === member.selected ) ) {
			isValid = false;
		} else {
			forEach( members, member => {
				if( member.selected ) {
					
					// check if at least one value field is selected
					const selectedField = find( member, { selected: true } );
					if( isNil( selectedField ) ) {
						isValid = false;
						return; // close `forEach` loop
					}
	
					// check if `definedAmount` value is present if selected
					if( get( member, 'definedAmount.selected' ) && ( isEmpty( get( member, 'definedAmount.value' ) ) || ( 0 === toNumber( get( member, 'definedAmount.value' ) ) ) ) ) {
						isValid = false;
						return; // close `forEach` loop
					}
				}

			} );
		}


		this.props.onAmountChange( {
			isValid: isValid,
			selection: map( filter( members, _member => _member.selected ), member => {

				// get selected value field
				const selectedField = find( member, { selected: true } );

				return {
					memberId: member.id,
					amount: get( selectedField, 'value', null ),
				};
			} ),
		} );
	}

	/*********************************************/

	/**
	 * @desc When component unmounts, cleanup resouces.
	 */
	componentWillUnmount() {
		this.memberDetailsPayBalanceSetAmountService.cancel(); // cancel ongoing requests
	}

	/**
	 * @desc Render a React view component
	 */
	render() {
		return (
			<MemberDetailsPayBalanceSetAmountView
				isFetching={ this.state.isFetching }
				networkStatus={ this.state.networkStatus }
				{ ...get( this.state, 'componentData', {} ) }
				onMemberSelect={ this.handleMemberSelect }
				onRadioFieldSelect={ this.handleRadioFieldSelect }
				onDefinedAmountChange={ this.handleDefinedAmountChange }
			/>
		);
	}
}

// set display name
_MemberDetailsPayBalanceSetAmount.displayName = 'MemberDetailsPayBalanceSetAmount';

// set default props
_MemberDetailsPayBalanceSetAmount.defaultProps = {
	requestPayload: {},
	onAmountChange: noop,
};

// prop types
_MemberDetailsPayBalanceSetAmount.propTypes = {};

/*********************************************/

// wrap with router
export const MemberDetailsPayBalanceSetAmount = withRouter( _MemberDetailsPayBalanceSetAmount );
